import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import { useParams } from "react-router-dom";

import LotDetails from "./LotDetails/View";
import BaseField from "./base-field";

// LOG COMPONENTS
import BidLogs from "./logs/bidlogs";
import AbsenteeLogs from "./logs/absentee";

import { GetAuctionByID } from "../../../adminModel/auctions";
import { findlot } from "../../../adminModel/lots";

/* =========================================================
   TAB PANEL
========================================================= */

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography component="div">
            {children}
          </Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

/* =========================================================
   ACCESSIBILITY
========================================================= */

const a11yProps = (index) => ({
  id: `simple-tab-${index}`,
  "aria-controls": `simple-tabpanel-${index}`,
});

/* =========================================================
   STYLES
========================================================= */

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: "#fff",
  },

  /* =======================================================
     HEADER
  ======================================================= */

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    backgroundColor: "#fff",
    borderBottom: "1px solid #e0e0e0",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },

  /* =======================================================
     LOT INFO
  ======================================================= */

  lotInfoWrapper: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: 1,
  },

  lotImageWrapper: {
    width: 64,
    height: 64,
    minWidth: 64,
    marginRight: theme.spacing(2),
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#f3f4f6",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  lotImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  noImage: {
    fontSize: 11,
    color: "#999",
    textAlign: "center",
  },

  lotInfo: {
    minWidth: 0,
  },

  lotNumber: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.primary.main,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    marginBottom: theme.spacing(0.5),
  },

  lotTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#222",
    lineHeight: 1.4,
    wordBreak: "break-word",

    [theme.breakpoints.down("sm")]: {
      fontSize: 18,
    },
  },

  auctionName: {
    marginTop: theme.spacing(0.5),
    fontSize: 13,
    color: "#666",
  },

  auctionLabel: {
    fontWeight: 600,
    color: "#444",
  },

  /* =======================================================
     NAVIGATION
  ======================================================= */

  navigation: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(3),
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginTop: theme.spacing(2),
      width: "100%",
    },
  },

  navigationButton: {
    minWidth: 100,
    height: 38,
    textTransform: "none",
    fontWeight: 600,

    [theme.breakpoints.down("sm")]: {
      flex: 1,
    },
  },

  nextButton: {
    marginLeft: theme.spacing(1),
  },

  /* =======================================================
     TABS
  ======================================================= */

  tabs: {
    backgroundColor: "#fff",
    borderBottom: "1px solid #e0e0e0",

    "& .MuiTab-root": {
      minHeight: 52,
      textTransform: "none",
      fontWeight: 600,
      fontSize: 14,
      color: "#555",
    },

    "& .Mui-selected": {
      color: theme.palette.primary.main,
    },

    [theme.breakpoints.down("sm")]: {
      "& .MuiTab-root": {
        minWidth: 0,
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
        fontSize: 12,
      },
    },
  },

  tabContent: {
    backgroundColor: "#fff",
  },
}));

/* =========================================================
   COMPONENT
========================================================= */

export default function SimpleTabs() {
  const classes = useStyles();

  const [value, setValue] = useState(0);

  const [auctionData, setAuctionData] = useState({});
  const [lotData, setLotData] = useState({});

  const {
    eventName,
    eventId,
    something,
    lotId,
    lotNumber,
  } = useParams();

  /* =======================================================
     GET AUCTION
  ======================================================= */

  useEffect(() => {
    if (!eventId) {
      return;
    }

    const fetchAuction = async () => {
      try {
        const result = await GetAuctionByID(eventId);

        if (result) {
          setAuctionData(result);
        } else {
          setAuctionData({});
        }
      } catch (error) {
        console.error(
          "Failed to fetch auction details:",
          error
        );

        setAuctionData({});
      }
    };

    fetchAuction();
  }, [eventId]);

  /* =======================================================
     GET LOT
  ======================================================= */

  useEffect(() => {
    if (!eventId || !something) {
      return;
    }

    
    const fetchLot = async () => {
  
      try {
        const result = await findlot(
         
          something,
           eventId
        );

        if (
          result &&
          result.data &&
          result.data.length > 0
        ) {
          setLotData(result.data[0]);
          console.log(result.data[0])
        } else {
          setLotData({});
        }
      } catch (error) {
        console.error(
          "Failed to fetch lot details:",
          error
        );

        setLotData({});
      }
    };

    fetchLot();
  }, [eventId, something]);

  /* =======================================================
     RESTORE TAB
     
     0 = Base Fields
     1 = Lot Details
     2 = Bid Logs
     3 = Absentee Logs
  ======================================================= */

  useEffect(() => {
    const savedTab = localStorage.getItem(
      "selectedTabIndex"
    );

    if (savedTab !== null) {
      const parsedTab = parseInt(savedTab, 10);

      if (parsedTab >= 0 && parsedTab <= 3) {
        setValue(parsedTab);
      }
    }
  }, []);

  /* =======================================================
     TAB CHANGE
  ======================================================= */

  const handleChange = (event, newValue) => {
    setValue(newValue);

    localStorage.setItem(
      "selectedTabIndex",
      newValue
    );
  };

  /* =======================================================
     NEXT LOT
  ======================================================= */

  const handleNext = () => {
    const currentLot = parseInt(lotNumber, 10);
    const currentSomething = parseInt(something, 10);

    if (Number.isNaN(currentLot)) {
      return;
    }

    const nextLotNumber = currentLot + 1;

    const nextSomething =
      Number.isNaN(currentSomething)
        ? something
        : currentSomething + 1;

    window.location.href =
      `/app/lot/explore/` +
      `${encodeURIComponent(eventName)}/` +
      `${nextLotNumber}/` +
      `${eventId}/` +
      `${nextSomething}/` +
      `${lotId}`;
  };

  /* =======================================================
     PREVIOUS LOT
  ======================================================= */

  const handlePrevious = () => {
    const currentLot = parseInt(lotNumber, 10);
    const currentSomething = parseInt(something, 10);

    if (Number.isNaN(currentLot)) {
      return;
    }

    const previousLotNumber = currentLot - 1;

    const previousSomething =
      Number.isNaN(currentSomething)
        ? something
        : currentSomething - 1;

    if (previousLotNumber < 1) {
      return;
    }

    window.location.href =
      `/app/lot/explore/` +
      `${encodeURIComponent(eventName)}/` +
      `${previousLotNumber}/` +
      `${eventId}/` +
      `${previousSomething}/` +
      `${lotId}`;
  };

  /* =======================================================
     DISPLAY VALUES
  ======================================================= */

  const displayLotNumber =
    lotNumber &&
    lotNumber !== "undefined"
      ? lotNumber
      : "-";

  const displayEventName =
    eventName &&
    eventName !== "undefined"
      ? decodeURIComponent(eventName)
      : "Lot Details";

  const displayAuctionName =
    auctionData.auction_name || "N/A";

  /* =======================================================
     LOT IMAGE
  ======================================================= */

  const lotImage =
    lotData.image ||
    lotData.image_url ||
    lotData.imageUrl ||
    lotData.photo ||
    lotData.photo_url ||
    lotData.thumbnail ||
    lotData.thumbnail_url ||
    lotData.lot_image ||
    lotData.lot_image_url ||
    "";

  /* =======================================================
     FIRST LOT
  ======================================================= */

  const isFirstLot =
    parseInt(lotNumber, 10) === 1;

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className={classes.root}>

      {/* ===================================================
          HEADER
      =================================================== */}

      <Box className={classes.header}>

        {/* LOT INFO */}
        <Box className={classes.lotInfoWrapper}>

          {/* IMAGE */}
          <Box className={classes.lotImageWrapper}>
            {lotImage ? (
              <img
                src={lotImage}
                alt={`Lot ${displayLotNumber}`}
                className={classes.lotImage}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <Typography className={classes.noImage}>
                No Image
              </Typography>
            )}
          </Box>

          {/* TEXT */}
          <Box className={classes.lotInfo}>

            <Typography className={classes.lotNumber}>
              Lot {displayLotNumber}
            </Typography>

            <Typography className={classes.lotTitle}>
              {displayEventName}
            </Typography>

            <Typography className={classes.auctionName}>
              <span className={classes.auctionLabel}>
                Auction:
              </span>{" "}
              {displayAuctionName}
            </Typography>

          </Box>

        </Box>

        {/* PREVIOUS / NEXT */}
        <Box className={classes.navigation}>

          <Button
            variant="outlined"
            color="primary"
            size="small"
            className={classes.navigationButton}
            disabled={isFirstLot}
            onClick={handlePrevious}
          >
            Previous
          </Button>

          <Button
            variant="contained"
            color="primary"
            size="small"
            className={`${classes.navigationButton} ${classes.nextButton}`}
            onClick={handleNext}
          >
            Next
          </Button>

        </Box>

      </Box>

      {/* ===================================================
          TABS
      =================================================== */}

      <AppBar
        position="static"
        elevation={0}
        color="transparent"
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Lot details tabs"
          className={classes.tabs}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >

          <Tab
            label="Base Fields"
            {...a11yProps(0)}
          />

          <Tab
            label="Lot Details"
            {...a11yProps(1)}
          />

          <Tab
            label="Bid Logs"
            {...a11yProps(2)}
          />

          <Tab
            label="Absentee Logs"
            {...a11yProps(3)}
          />

        </Tabs>
      </AppBar>

      {/* ===================================================
          TAB CONTENT
      =================================================== */}

      <div className={classes.tabContent}>

        {/* BASE FIELDS */}
        <TabPanel value={value} index={0}>
          <BaseField
            auctionID={eventId}
            something={something}
          />
        </TabPanel>

        {/* LOT DETAILS */}
        <TabPanel value={value} index={1}>
          <LotDetails
            eventId={eventId}
            lotId={lotId}
            something={something}
          />
        </TabPanel>

        {/* BID LOGS */}
        <TabPanel value={value} index={2}>
          <BidLogs
            eventId={eventId}
            something={something}
          />
        </TabPanel>

        {/* ABSENTEE LOGS */}
        <TabPanel value={value} index={3}>
          <AbsenteeLogs
            eventId={eventId}
            something={something}
          />
        </TabPanel>

      </div>

    </div>
  );
}